import React, { useEffect, useRef, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { stripeService } from '../services';
import gatheringService from '../services/gatheringService';
import { isStripeDevMode, mockConfirmCardSetup } from '../utils/stripeDevMode';
import DevCardElement from './DevCardElement';
import CardPaymentSection from './CardPaymentSection';
import { RingCta, kicker } from './gathering/gatheringUi';
import { plural } from '../utils/surfaceFormat';

function formatCents(cents) {
  return `$${(Math.abs(cents || 0) / 100).toFixed(2)}`;
}

export const REASON_LABEL = {
  free: "Free · you'll be marked going",
  already_have: 'You already have a ticket',
  needs_approval: 'Needs host approval · apply on the event',
  external: 'Sold off-platform',
};

const MUTED = '#6B7280';
/** White rows on a #F2F2F2 2px-gap stack (the sheet's lines block). */
export function SheetLines({ rows, total, testId }) {
  return (
    <div className="flex flex-col font-mono" style={{ gap: 2, background: '#F2F2F2' }} data-testid={testId}>
      {rows.map((r) => (
        <span key={r.key} className="flex justify-between bg-white" style={{ gap: 12, padding: '12px 14px', fontSize: 12 }}>
          <span className="flex min-w-0 flex-col" style={{ gap: 2 }}>
            <span style={{ fontWeight: 700 }}>{r.title}</span>
            {r.sub && <span style={{ fontSize: 10, color: MUTED }}>{r.sub}</span>}
          </span>
          <span style={{ fontWeight: 700, flexShrink: 0 }}>{r.value}</span>
        </span>
      ))}
      {total != null && (
        <span className="flex justify-between bg-white" style={{ padding: 14, borderTop: '2px solid #000', fontSize: 14, fontWeight: 800, letterSpacing: '.06em' }}>
          <span>TOTAL ON CARD</span><span>{total}</span>
        </span>
      )}
    </div>
  );
}
/** The #F5F5F5 radius-18 notes box. */
export function SheetNotes({ notes }) {
  if (!notes.length) return null;
  return (
    <div className="flex flex-col font-mono" style={{ gap: 6, padding: '12px 14px', borderRadius: 18, background: '#F5F5F5' }} data-testid="sheet-notes">
      {notes.map((n) => (
        <span key={n.key} className="flex justify-between" style={{ gap: 12, fontSize: 11 }}>
          <span className="min-w-0">{n.title}</span>
          <span style={{ flexShrink: 0, color: MUTED }}>{n.value}</span>
        </span>
      ))}
    </div>
  );
}
/** `PAY WITH CARD` + the card fields, styled as pill inputs. */
export function SheetCard({ children }) {
  return (
    <div className="flex flex-col font-mono gathering-card-fields" style={{ gap: 8 }}>
      <span style={{ ...kicker(), color: MUTED }}>PAY WITH CARD</span>
      {children}
    </div>
  );
}
export function SheetError({ children }) {
  return <div className="font-mono" style={{ padding: '10px 14px', borderRadius: 14, background: '#FEE2E2', color: '#991B1B', fontSize: 11 }}>{children}</div>;
}

/**
 * GatheringCheckoutForm — buy every ticketed event in a gathering in one payment.
 * Each event is charged individually on the server (its own ticket + record), so
 * this is card-only (a shared wallet can't be split accurately across N charges).
 *
 * Props: gatheringId, userEmail, onDone(lines), onError(message)
 */
export default function GatheringCheckoutForm({ gatheringId, userEmail, onDone, onError }) {
  const stripe = useStripe();
  const cardRef = useRef(null);
  const [cardReady, setCardReady] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [devBehavior, setDevBehavior] = useState('success');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await gatheringService.checkoutAllQuote(gatheringId);
      if (cancelled) return;
      if (!r.success) setError(r.error?.message || r.error || 'Unable to load quote');
      else setQuote(r.data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [gatheringId]);

  async function pay(e) {
    e?.preventDefault?.();
    if (!quote) return;
    setSubmitting(true);
    setError(null);

    let stripeCustomerId = null;
    let stripePaymentMethodId = null;
    const needsCard = (quote.cardPortionCents || 0) > 0;

    if (needsCard) {
      if (isStripeDevMode()) {
        const mock = await mockConfirmCardSetup(devBehavior);
        stripePaymentMethodId = mock.setupIntent.payment_method;
        stripeCustomerId = `cus_dev_${gatheringId}`;
      } else {
        if (!stripe) { setSubmitting(false); setError('Stripe is not loaded'); return; }
        const setupRes = await stripeService.createSetupIntent();
        if (!setupRes.success) { setSubmitting(false); setError(setupRes.error || 'Could not prepare payment'); return; }
        if (!cardRef.current) { setSubmitting(false); setError('Card form is not ready'); return; }
        const result = await cardRef.current.confirmSetup(setupRes.data.clientSecret, { email: userEmail });
        if (result.error) { setSubmitting(false); setError(result.error); return; }
        stripePaymentMethodId = result.paymentMethod;
        stripeCustomerId = setupRes.data.customerId;
      }
    }

    const res = await gatheringService.checkoutAll(gatheringId, { stripeCustomerId, stripePaymentMethodId, skipBalance: true });
    setSubmitting(false);
    if (!res.success) { setError(res.error?.message || res.error || 'Checkout failed'); onError?.(res.error); return; }
    onDone?.(res.data?.lines || []);
  }

  if (loading) return <div className="font-mono" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>LOADING…</div>;
  if (!quote) return <SheetError>{error || 'Unable to load quote'}</SheetError>;

  const chargeable = quote.events || [];
  const notes = [...(quote.free || []), ...(quote.skipped || [])].map((n) => ({ key: n.eventId, title: n.title, value: REASON_LABEL[n.reason] || n.reason }));

  if (chargeable.length === 0) {
    return (
      <div className="flex flex-col font-mono" style={{ gap: 12, fontSize: 13 }}>
        <p style={{ margin: 0, color: '#374151' }}>Nothing to buy right now.</p>
        <SheetNotes notes={notes} />
      </div>
    );
  }

  return (
    <form onSubmit={pay} className="flex flex-col" style={{ gap: 16 }}>
      <SheetLines
        rows={chargeable.map((e) => ({ key: e.eventId, title: e.title, sub: `${formatCents(e.ticketCents)} ticket + fees`, value: formatCents(e.grandTotalCents) }))}
        total={formatCents(quote.grandTotalCents)}
        testId="checkout-lines"
      />
      <SheetNotes notes={notes} />
      <SheetCard>
        {isStripeDevMode() ? (
          <DevCardElement value={devBehavior} onChange={setDevBehavior} />
        ) : (
          <CardPaymentSection ref={cardRef} amountCents={quote.grandTotalCents} currency="cad" countryDefault="CA" onValidityChange={setCardReady} disabled={submitting} label={null} appearance="pill" />
        )}
      </SheetCard>
      {error && <SheetError>{error}</SheetError>}
      <RingCta type="submit" disabled={submitting || (!isStripeDevMode() && !cardReady)} data-testid="checkout-pay">
        {submitting ? 'PROCESSING…' : `PAY ${formatCents(quote.grandTotalCents)}`}
      </RingCta>
      <span className="font-mono" style={{ fontSize: 11, lineHeight: 1.6, color: MUTED }}>
        {plural(chargeable.length, 'separate charge').toLowerCase()} on one card. Each event's money sits in its own escrow.
      </span>
    </form>
  );
}
